"use strict";

(function () {
  const RECENTLY_VIEWED_KEY = "rocco_recently_viewed_products";

  function parseList(raw) {
    if (!raw) return [];
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function getRecentlyViewedHandles() {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || "[]");
      if (!Array.isArray(stored)) return [];
      return stored.filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function getShopifyRoot(contextEl) {
    const fromDataset = contextEl && contextEl.dataset.shopifyRoot;
    if (fromDataset) {
      return fromDataset.endsWith("/") ? fromDataset : `${fromDataset}/`;
    }
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  /** Product JSON API returns default-locale URLs; build locale-aware URLs like Liquid product.url. */
  function getLocalizedProductUrl(product, contextEl) {
    if (!product || !product.handle) return "#";
    return `${getShopifyRoot(contextEl)}products/${product.handle}`;
  }

  function localizeProduct(product, contextEl) {
    if (!product) return product;
    return Object.assign({}, product, { url: getLocalizedProductUrl(product, contextEl) });
  }

  function formatMoney(cents) {
    if (typeof cents !== "number") return "";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD",
    }).format(cents / 100).replace(/\.00$/, '').replace(/,00$/, '');
  }

  function handleize(str) {
    return String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function parseCustomColors(raw) {
    const map = {};
    if (!raw) return map;
    raw.split("\n").forEach((line) => {
      const colon = line.indexOf(":");
      if (colon === -1) return;
      const label = line.slice(0, colon).trim();
      const value = line.slice(colon + 1).trim();
      if (!label || !value) return;
      const keys = [
        handleize(label),
        label.toLowerCase(),
        handleize(label).replace(/-/g, ""),
      ].filter(Boolean);
      keys.forEach((key) => {
        if (!map[key]) map[key] = value;
      });
    });
    return map;
  }

  const DEFAULT_COLOR_TRIGGERS = [
    "color",
    "colour",
    "couleur",
    "colore",
    "farbe",
    "cor",
    "צבע",
  ];

  function getSwatchConfig(wrapper) {
    const sectionRoot = wrapper.closest(".popular-products");
    const configScript = sectionRoot
      ? sectionRoot.querySelector("[data-popular-products-swatch-config]")
      : null;

    if (configScript) {
      try {
        const parsed = JSON.parse(configScript.textContent);
        const triggers = Array.isArray(parsed.triggers) ? parsed.triggers.filter(Boolean) : [];
        return {
          sectionId: parsed.sectionId || "mix",
          showSecondaryImage: !!parsed.showSecondaryImage,
          type: parsed.type || "custom",
          triggers: triggers.length ? triggers : DEFAULT_COLOR_TRIGGERS,
          borderRadius: Number(parsed.borderRadius || 0),
          customColors: parseCustomColors(parsed.customColors || ""),
        };
      } catch (error) {
        /* fall through to defaults */
      }
    }

    return {
      sectionId: "mix",
      showSecondaryImage: true,
      type: "custom",
      triggers: DEFAULT_COLOR_TRIGGERS,
      borderRadius: 0,
      customColors: {},
    };
  }

  function getProductOptionMeta(product, index) {
    const raw = product.options[index];
    if (raw && typeof raw === "object") {
      return {
        name: raw.name || "",
        position: raw.position || index + 1,
        values: Array.isArray(raw.values) ? raw.values.filter(Boolean) : null,
      };
    }
    return {
      name: String(raw || ""),
      position: index + 1,
      values: null,
    };
  }

  function optionMatchesColorTrigger(optionName, triggers) {
    const name = String(optionName || "").trim();
    const nameHandle = handleize(name);
    const nameLower = name.toLowerCase();
    return triggers.some((trigger) => {
      const t = String(trigger || "").trim();
      if (!t) return false;
      const triggerHandle = handleize(t);
      if (nameHandle && triggerHandle && nameHandle === triggerHandle) return true;
      if (nameLower === t.toLowerCase()) return true;
      if (!triggerHandle && name === t) return true;
      return false;
    });
  }

  function isValidCssColor(value) {
    if (!value || !String(value).trim()) return false;
    const v = String(value).trim();
    return (
      v.includes("#") ||
      v.startsWith("rgb") ||
      v.includes("url(") ||
      v.includes("gradient")
    );
  }

  function getVariantSwatchImageUrl(product, optionKey, value) {
    const variant = findVariantForOptionValue(product, optionKey, value);
    if (!variant) return null;
    const img = variant.featured_image;
    if (!img) return null;
    return typeof img === "string" ? img : img.src || null;
  }

  function getColorValue(colorName, customColors, product, optionKey) {
    const raw = String(colorName || "").trim();
    const keys = [handleize(raw), raw.toLowerCase(), handleize(raw).replace(/-/g, "")].filter(Boolean);
    for (let i = 0; i < keys.length; i++) {
      if (customColors[keys[i]]) return customColors[keys[i]];
    }
    const variantImage =
      product && optionKey ? getVariantSwatchImageUrl(product, optionKey, raw) : null;
    if (variantImage) return `url(${variantImage}) no-repeat center/cover`;
    return "";
  }

  function findVariantForOptionValue(product, optionKey, value) {
    if (!product.variants) return null;
    for (let i = 0; i < product.variants.length; i++) {
      const variant = product.variants[i];
      if (!variant.available) continue;
      if (variant[optionKey] === value) return variant;
    }
    for (let i = 0; i < product.variants.length; i++) {
      const variant = product.variants[i];
      if (variant[optionKey] === value) return variant;
    }
    return null;
  }

  function findColorOption(product, config) {
    if (!product.options || !product.options.length || !product.variants || !product.variants.length) {
      return null;
    }
    if (product.variants.length === 1 && product.variants[0].title === "Default Title") {
      return null;
    }

    for (let i = 0; i < product.options.length; i++) {
      const meta = getProductOptionMeta(product, i);
      if (!meta.name || !optionMatchesColorTrigger(meta.name, config.triggers)) continue;

      const optionKey = `option${meta.position}`;
      const values =
        meta.values && meta.values.length
          ? meta.values
          : [
              ...new Set(
                product.variants.map((v) => v[optionKey]).filter((v) => v != null && v !== "")
              ),
            ];

      if (values.length > 1) {
        return { name: meta.name, position: meta.position, optionKey, values };
      }
    }
    return null;
  }

  function buildColorSwatchesHTML(product, colorOption, config, contextEl) {
    const productId = product.id;
    const sectionId = config.sectionId;
    let html =
      '<div class="product-form__controls js-color-swatches"><div class="product-form__controls-group">';

    const productUrl = getLocalizedProductUrl(product, contextEl);

    colorOption.values.forEach((value, index) => {
      const variant = findVariantForOptionValue(product, colorOption.optionKey, value);
      const variantLink = variant
        ? `${productUrl}?variant=${variant.id}`
        : productUrl;
      const variantId = variant ? variant.id : "";
      const inputId = `${sectionId}-${colorOption.position}-${index}-${productId}`;
      const inputName = `${colorOption.name}-${sectionId}-${productId}`;
      const colorValue = getColorValue(value, config.customColors, product, colorOption.optionKey);
      const checked = index === 0 ? " checked" : "";
      const swatchUsesImage = isValidCssColor(colorValue) && colorValue.includes("url(");

      html += `<input type="radio" id="${inputId}" name="${escapeHtml(inputName)}" value="${escapeHtml(value)}" data-variant-link="${escapeHtml(variantLink)}" data-variant-id="${variantId}"${checked}>`;
      if (swatchUsesImage) {
        html += `<label for="${inputId}" class="variant-swatch variant-swatch--square focus-inset" style="background-image: ${colorValue}; border-radius: ${config.borderRadius}px; background-position: center; background-repeat: no-repeat; background-size: cover;" title="${escapeHtml(value)}"><span class="visually-hidden">${escapeHtml(value)}</span></label>`;
      } else {
        const cssColor = isValidCssColor(colorValue) ? colorValue : "#e5e5e5";
        html += `<label for="${inputId}" class="color-swatch focus-inset" style="--swatch-color: ${cssColor}; border-radius: ${config.borderRadius}px" title="${escapeHtml(value)}"><span class="visually-hidden">${escapeHtml(value)}</span></label>`;
      }
    });

    html += "</div></div>";
    return html;
  }

  function detectTemplateProductId(template) {
    const quickAddInfo = template.querySelector('[id^="QuickAddInfo-"]');
    if (quickAddInfo) {
      const m = quickAddInfo.id.match(/QuickAddInfo-(.+)$/);
      if (m) return m[1];
    }
    const modalOpener = template.querySelector('modal-opener[data-modal^="#QuickAdd-"]');
    if (modalOpener) {
      const m = modalOpener.getAttribute("data-modal").match(/#QuickAdd-(.+)$/);
      if (m) return m[1];
    }
    const quickAddModal = template.querySelector('quick-add-modal[id^="QuickAdd-"]');
    if (quickAddModal) {
      const m = quickAddModal.id.match(/QuickAdd-(.+)$/);
      if (m) return m[1];
    }
    const submitBtn = template.querySelector('button[id^="quick-add-"][id$="-submit"]');
    if (submitBtn) {
      const m = submitBtn.id.match(/^quick-add-.*?(\d+)-submit$/);
      if (m) return m[1];
    }
    return null;
  }

  function rewriteAttributeIds(root, oldId, newId) {
    if (!oldId || oldId === newId) return;
    const ATTRS = ["id", "for", "aria-labelledby", "aria-controls", "aria-describedby", "name"];
    const all = [root, ...root.querySelectorAll("*")];
    all.forEach((el) => {
      ATTRS.forEach((attr) => {
        const val = el.getAttribute && el.getAttribute(attr);
        if (val && val.indexOf(oldId) !== -1) {
          el.setAttribute(attr, val.split(oldId).join(newId));
        }
      });
    });
  }

  function resolveQuickAddModal(opener) {
    const selector = opener.getAttribute("data-modal");
    if (selector) {
      const direct = document.querySelector(selector);
      if (direct && typeof direct.show === "function") return direct;
    }
    const fallback = document.querySelector("quick-add-modal");
    if (fallback && typeof fallback.show === "function") return fallback;
    return null;
  }

  function buildPriceMarkup(product) {
    const moneyPrice = formatMoney(product.price);
    const compareAt = typeof product.compare_at_price === "number" ? product.compare_at_price : 0;
    const onSale = compareAt > product.price;
    const moneyCompare = onSale ? formatMoney(compareAt) : "";

    return `
      <div class="price${onSale ? " price--on-sale" : ""}">
        <dl>
          <div class="price__regular">
            <dd class="price__last">
              <span class="price-item price-item--regular">${moneyPrice}</span>
            </dd>
          </div>
          <div class="price__sale">
            <dd class="price__last">
              <span class="price-item price-item--sale">${moneyPrice}</span>
            </dd>
            <dd class="price__compare">
              <s class="price-item price-item--regular">${moneyCompare}</s>
            </dd>
          </div>
        </dl>
      </div>
    `;
  }

  function updateCardMedia(media, product, config, colorOption) {
    const objectFit =
      (media.querySelector("img") && media.querySelector("img").style.objectFit) || "cover";

    media.querySelectorAll("img").forEach((img) => img.remove());
    media.querySelectorAll("svg.placeholder-svg").forEach((svg) => svg.remove());

    const productImages = Array.isArray(product.images) ? product.images : [];
    let firstSrc = product.featured_image || productImages[0];

    if (colorOption) {
      const firstVariant = findVariantForOptionValue(
        product,
        colorOption.optionKey,
        colorOption.values[0]
      );
      if (firstVariant && firstVariant.featured_image) {
        firstSrc =
          typeof firstVariant.featured_image === "string"
            ? firstVariant.featured_image
            : firstVariant.featured_image.src;
      }
    }

    if (firstSrc) {
      const first = document.createElement("img");
      first.src = typeof firstSrc === "string" ? firstSrc : firstSrc.src || "";
      first.alt = product.title || "";
      first.loading = "lazy";
      first.className = "motion-reduce media--first";
      first.style.objectFit = objectFit;
      media.appendChild(first);
    }

    if (config.showSecondaryImage) {
      const normalize = (s) => String(s || "").replace(/^https?:/, "");
      const firstNorm = normalize(firstSrc);
      const secondSrc = productImages.find((src) => src && normalize(src) !== firstNorm);
      if (secondSrc) {
        const second = document.createElement("img");
        second.src = secondSrc;
        second.alt = product.title || "";
        second.loading = "lazy";
        second.className = "motion-reduce media--second";
        second.style.objectFit = objectFit;
        media.appendChild(second);
      }
    }
  }

  function buildViewedCardFromTemplate(template, product, config, contextEl) {
    const item = template.cloneNode(true);
    item.querySelectorAll(".js-color-swatches input").forEach((input) => {
      delete input.dataset.swatchBound;
    });
    const colorOption = findColorOption(product, config);

    item.removeAttribute("data-best-seller-handle");
    item.setAttribute("data-viewed-handle", product.handle);

    const oldProductId = detectTemplateProductId(item);
    const newProductId = String(product.id);
    rewriteAttributeIds(item, oldProductId, newProductId);

    const cardWrapper = item.querySelector(".card-wrapper");
    if (cardWrapper) {
      cardWrapper.setAttribute("data-product", product.handle);
      cardWrapper.classList.add("js-color-swatches-wrapper");
    }

    const hiddenTitle = item.querySelector(".card-wrapper > .visually-hidden");
    if (hiddenTitle) hiddenTitle.textContent = product.title || "";

    const titleLink = item.querySelector(".card__title a");
    if (titleLink) {
      titleLink.textContent = product.title || "";
      titleLink.setAttribute("title", product.title || "");
      titleLink.setAttribute("href", product.url || "#");
    }

    const overlayLink = item.querySelector(".card-wrapper__link--overlay");
    if (overlayLink) {
      overlayLink.setAttribute("href", product.url || "#");
      overlayLink.classList.add("js-color-swatches-link");
    }

    const media = item.querySelector(".card__inner .media");
    if (media) {
      updateCardMedia(media, product, config, colorOption);
    }

    const priceEl = item.querySelector(".card-information .price");
    if (priceEl) {
      priceEl.outerHTML = buildPriceMarkup(product);
    }

    let swatches = item.querySelector(".product-parameters");
    if (!swatches) {
      const cardInfo = item.querySelector(".card-information__wrapper");
      if (cardInfo) {
        swatches = document.createElement("div");
        swatches.className = "product-parameters";
        const priceBlock = cardInfo.querySelector(".price");
        if (priceBlock) {
          priceBlock.insertAdjacentElement("afterend", swatches);
        } else {
          cardInfo.appendChild(swatches);
        }
      }
    }
    if (swatches) {
      if (colorOption) {
        swatches.innerHTML = buildColorSwatchesHTML(product, colorOption, config, contextEl);
      } else {
        swatches.innerHTML = "";
      }
    }

    const quickViewBtn = item.querySelector("modal-opener button[data-product-url]");
    if (quickViewBtn) quickViewBtn.setAttribute("data-product-url", product.url || "#");

    const variantInput = item.querySelector('product-form form input[name="id"]');
    if (variantInput && product.variants && product.variants[0]) {
      const defaultVariant = colorOption
        ? findVariantForOptionValue(product, colorOption.optionKey, colorOption.values[0])
        : product.variants[0];
      if (defaultVariant) variantInput.value = defaultVariant.id;
    }

    item.querySelectorAll(".badge--sale, .badge--soldout, .badge--custom").forEach((el) => el.remove());

    const recentlyViewedBadge = item.querySelector(".badge--recently-viewed");
    if (recentlyViewedBadge) {
      recentlyViewedBadge.style.display = "";
      recentlyViewedBadge.setAttribute("data-product-handle", product.handle);
    } else {
      const badgeContainer = item.querySelector(".card__badge");
      if (badgeContainer) {
        badgeContainer.insertAdjacentHTML(
          "beforeend",
          `<span class="badge badge--recently-viewed" aria-hidden="true" data-product-handle="${product.handle}">Recently Viewed</span>`
        );
      }
    }

    rewireQuickViewElements(item);

    return item;
  }

  function rewireQuickViewElements(root) {
    root.querySelectorAll("modal-opener").forEach((opener) => {
      const button = opener.querySelector("button");
      if (!button || button.dataset.viewedQuickViewBound === "true") return;
      button.dataset.viewedQuickViewBound = "true";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const modal = resolveQuickAddModal(opener);
        if (modal) modal.show(button);
      });
    });
  }

  function buildViewedCardFallback(product) {
    const image = product && product.featured_image;
    const imageHtml = image
      ? `<img src="${image}" alt="${product.title || ""}" loading="lazy" class="popular-products__mix-image">`
      : "";

    const wrapper = document.createElement("li");
    wrapper.className = "popular-products__item collection-product-card popular-products__mix-item";
    wrapper.setAttribute("data-viewed-handle", product.handle);
    wrapper.innerHTML = `
      <a class="popular-products__mix-link full-unstyled-link" href="${product.url}">
        <div class="popular-products__mix-media">${imageHtml}</div>
        <div class="popular-products__mix-information">
          <p class="popular-products__mix-title">${product.title || ""}</p>
          <p class="popular-products__mix-price">${formatMoney(product.price)}</p>
        </div>
      </a>
    `;
    return wrapper;
  }

  function initColorSwatchesForWrapper(wrapper) {
    if (typeof window.initColorSwatches === "function") {
      window.initColorSwatches();
    }
  }

  function initPopularProductsMix(wrapper) {
    if (wrapper.dataset.popularProductsMixInitialized === "true") return;
    wrapper.dataset.popularProductsMixInitialized = "true";

    const config = getSwatchConfig(wrapper);
    const totalToShow = Number(wrapper.dataset.popularProductsLimit || 4);
    if (!totalToShow) return;

    const bestSellerHandles = parseList(wrapper.dataset.popularProductsBestSellers || "");
    if (!bestSellerHandles.length) return;

    const recentlyViewed = getRecentlyViewedHandles();
    if (!recentlyViewed.length) return;

    const viewedHandles = recentlyViewed.slice(0, Math.min(2, totalToShow));
    const bestSellerItems = Array.from(wrapper.querySelectorAll("[data-best-seller-handle]"));

    const cardTemplate = bestSellerItems[0] ? bestSellerItems[0].cloneNode(true) : null;
    if (cardTemplate) {
      cardTemplate.querySelectorAll(".js-color-swatches input").forEach((input) => {
        delete input.dataset.swatchBound;
      });
    }

    const bestSellerMap = new Map();
    bestSellerItems.forEach((item) => {
      bestSellerMap.set(item.dataset.bestSellerHandle, item);
    });

    bestSellerItems.forEach((item) => item.remove());

    const handlesToFetch = viewedHandles.filter((handle) => !bestSellerMap.has(handle));
    Promise.all(
      handlesToFetch.map((handle) =>
        fetch(`${getShopifyRoot(wrapper)}products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => (product ? localizeProduct(product, wrapper) : null))
          .catch(() => null)
      )
    ).then((products) => {
      const fetchedProductsMap = new Map();
      products.filter(Boolean).forEach((product) => fetchedProductsMap.set(product.handle, product));

      let viewedRenderedCount = 0;
      viewedHandles.forEach((handle) => {
        const bestSellerItem = bestSellerMap.get(handle);
        if (bestSellerItem) {
          bestSellerItem.removeAttribute("data-best-seller-handle");
          bestSellerItem.setAttribute("data-viewed-handle", handle);
          const badge = bestSellerItem.querySelector(".badge--recently-viewed");
          if (badge) badge.style.display = "";
          wrapper.appendChild(bestSellerItem);
          viewedRenderedCount += 1;
          return;
        }

        const fetchedProduct = fetchedProductsMap.get(handle);
        if (!fetchedProduct) return;

        const node = cardTemplate
          ? buildViewedCardFromTemplate(cardTemplate, fetchedProduct, config, wrapper)
          : buildViewedCardFallback(fetchedProduct);
        wrapper.appendChild(node);
        viewedRenderedCount += 1;
      });

      const bestSellerCount = Math.max(totalToShow - viewedRenderedCount, 0);
      const bestSellerHandlesToRender = bestSellerHandles
        .filter((handle) => !viewedHandles.includes(handle))
        .slice(0, bestSellerCount);

      bestSellerHandlesToRender.forEach((handle) => {
        const item = bestSellerMap.get(handle);
        if (item) wrapper.appendChild(item);
      });

      initColorSwatchesForWrapper(wrapper);
    });
  }

  function initAllPopularProductsMix() {
    document.querySelectorAll("[data-popular-products-mix]").forEach(initPopularProductsMix);
  }

  document.addEventListener("DOMContentLoaded", initAllPopularProductsMix);
  document.addEventListener("shopify:section:load", initAllPopularProductsMix);
})();
